import { useMemo, useState } from 'react';
import { Terrain } from '../composants/Terrain.jsx';
import { MONO, toucher } from '../composants/base.jsx';
import { Ballon, Bouclier, Inverser, Vers } from '../composants/icones.jsx';
import { placesEnPhase, formationDePhase } from '../logique/compo.js';
import { placesALaMinute } from '../logique/statistiques.js';
import { compteurs, score as scoreDe } from '../logique/match.js';
import { minutes as minutesDe } from '../local/chrono.js';

/**
 * LE TABLEAU TACTIQUE — téléphone à l'horizontale.
 *
 * Ce que la maquette voulait, et ce qu'elle voulait de trop. Le terrain plein écran,
 * le banc en rail, la bascule avec/sans ballon : gardés. Les courses fléchées de
 * chaque joueur et un curseur qui rejouerait leurs positions minute par minute :
 * écartés, parce qu'aucune source ne fournit une position par joueur et par minute et
 * qu'un curseur animerait donc une fiction dessinée à la main.
 *
 * Reste ce qui est vrai et se déduit sans rien inventer : QUI ÉTAIT SUR LE TERRAIN À
 * LA MINUTE N. Les changements le disent exactement. C'est la question qu'on se pose
 * en revoyant une action — « il était déjà entré, lui ? » — et elle a une réponse.
 */
export function EcranTactique({ instantane, compo, match, chrono, onCompo, onFermer }) {
  const [retourne, setRetourne] = useState(false);
  const [minute, setMinute] = useState(null);   // null = maintenant

  const effectif = instantane?.effectif ?? [];
  const parId = useMemo(() => new Map(effectif.map((j) => [j.id, j])), [effectif]);

  const enCours = chrono.etat !== 'arrete';
  const minuteMax = Math.max(90, enCours ? minutesDe(chrono) : 90);
  const minuteVue = minute ?? (enCours ? minutesDe(chrono) : 90);
  const auPresent = minute === null;

  // Le onze tel qu'il était à cette minute : les changements postérieurs sont ignorés,
  // ceux qui précèdent sont appliqués. Aucune interpolation, aucune supposition.
  const places = useMemo(
    () => placesALaMinute(placesEnPhase(compo), match.evenements, minuteVue),
    [compo, match.evenements, minuteVue]
  );

  const surLeTerrain = new Set(places.map((p) => p.joueurId).filter(Boolean));
  const banc = effectif.filter((j) => !surLeTerrain.has(j.id) && j.statut === 'ok').slice(0, 9);

  const c = compteurs(match);
  const s = scoreDe(match);
  const jalons = match.evenements.filter((e) => e.minute != null && e.minute <= 95);

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 70, display: 'flex',
      background: 'var(--bg)',
      paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)',
    }}>
      {/* Le banc en rail : en paysage la hauteur est rare et la largeur abonde, donc
          il passe sur le côté plutôt qu'en dessous. */}
      <div className="defile" style={{ width: 118, flex: 'none', borderRight: '1px solid var(--line)', background: 'var(--surf)', padding: '8px 7px' }}>
        <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '.9px', color: 'var(--ink2)', padding: '0 3px 6px' }}>BANC</div>
        {banc.map((j) => (
          <div key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 7px', marginBottom: 5, borderRadius: 10, background: 'var(--surf2)', border: '1px solid var(--line)' }}>
            <span style={{ width: 22, height: 22, flex: 'none', borderRadius: '50%', background: 'var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: 9.5 }}>{j.numero ?? '·'}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.patronyme ?? j.nom}</span>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '8px 10px' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>
            AJA <span style={{ fontFamily: MONO }}>{s.nous}</span>
            <span style={{ color: 'var(--ink3)' }}>–</span>
            <span style={{ fontFamily: MONO }}>{s.eux}</span>
            {compo.adversaire ? <span style={{ color: 'var(--ink2)', fontWeight: 600 }}> {compo.adversaire}</span> : null}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--ink2)' }}>
            {formationDePhase(compo.formation, compo.phase)} · CHG {c.changements}/5
          </span>

          <span style={{ flex: 1 }} />

          <div style={{ display: 'flex', padding: 2, borderRadius: 10, background: 'var(--surf2)', border: '1px solid var(--line)' }}>
            <Bascule actif={compo.phase === 'offensive'} onClick={() => onCompo({ ...compo, phase: 'offensive' })}><Ballon t={13} /></Bascule>
            <Bascule actif={compo.phase === 'defensive'} onClick={() => onCompo({ ...compo, phase: 'defensive' })}><Bouclier t={13} /></Bascule>
          </div>
          <BoutonPlat onClick={() => setRetourne((r) => !r)} aria-label="Inverser le sens"><Inverser t={15} /></BoutonPlat>
          <BoutonPlat onClick={onFermer} aria-label="Quitter le tableau tactique">Fermer</BoutonPlat>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Terrain
            style={{ flex: 1 }}
            paysage
            places={places}
            effectif={effectif}
            retourne={retourne}
            cibles={[]}
          />
        </div>

        {/* LA CHRONOLOGIE. Les jalons sont les événements réellement saisis, posés à
            leur minute. Faire glisser remonte le onze tel qu'il était alors. */}
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8 }}>
          <button
            onClick={() => { toucher(); setMinute(null); }}
            style={{
              flex: 'none', height: 30, padding: '0 10px', borderRadius: 9, fontSize: 11, fontWeight: 700,
              background: auPresent ? 'var(--acc)' : 'var(--surf2)',
              color: auPresent ? 'var(--tokink)' : 'var(--ink2)',
              border: `1px solid ${auPresent ? 'transparent' : 'var(--line)'}`,
            }}
          >{auPresent ? 'Maintenant' : 'Revenir'}</button>

          <div style={{ flex: 1, minWidth: 0, position: 'relative', height: 30 }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: 14, height: 2, background: 'var(--line)', borderRadius: 1 }} />
            <div style={{ position: 'absolute', left: 0, width: `${(minuteVue / minuteMax) * 100}%`, top: 14, height: 2, background: 'var(--acc)', borderRadius: 1 }} />
            {jalons.map((e) => (
              <span
                key={e.id}
                title={`${e.minute}'`}
                style={{
                  position: 'absolute', left: `${(e.minute / minuteMax) * 100}%`, top: 9,
                  width: 12, height: 12, marginLeft: -6, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: couleurJalon(e.type), color: 'var(--bg)',
                }}
              >{e.type === 'chg' ? <Vers t={9} /> : null}</span>
            ))}
            <input
              type="range" min="0" max={minuteMax} value={minuteVue}
              onChange={(ev) => setMinute(Number(ev.target.value))}
              aria-label="Minute du match"
              style={{ position: 'absolute', inset: 0, width: '100%', opacity: 0, cursor: 'pointer' }}
            />
            <span style={{
              position: 'absolute', left: `${(minuteVue / minuteMax) * 100}%`, top: 6,
              width: 18, height: 18, marginLeft: -9, borderRadius: '50%',
              background: 'var(--ink)', border: '2px solid var(--bg)', pointerEvents: 'none',
            }} />
          </div>

          <span style={{ flex: 'none', minWidth: 34, textAlign: 'right', fontFamily: MONO, fontWeight: 700, fontSize: 13 }}>{minuteVue}&apos;</span>
        </div>
      </div>
    </div>
  );
}

const couleurJalon = (t) => (t === 'but' ? 'var(--acc2)' : t === 'butAdverse' ? 'var(--bad)' : t === 'chg' ? 'var(--acc)' : t === 'rouge' ? 'var(--bad)' : 'var(--warn)');

const Bascule = ({ actif, children, ...r }) => (
  <button style={{ width: 32, height: 26, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: actif ? 'var(--acc)' : 'transparent', color: actif ? 'var(--tokink)' : 'var(--ink2)' }} {...r}>{children}</button>
);

const BoutonPlat = ({ children, ...r }) => (
  <button style={{ flex: 'none', height: 30, padding: '0 10px', borderRadius: 9, background: 'var(--surf2)', border: '1px solid var(--line)', fontSize: 11, fontWeight: 700, color: 'var(--ink2)', display: 'flex', alignItems: 'center', gap: 5 }} {...r}>{children}</button>
);
